import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost } from "../api";
import type { Datified, Schemas } from "../../api/types";
import { POLL_BASE_MS, POLL_LIVE_MS } from "./intervals";

export type Webhook = Datified<Schemas["Webhook"], "created_at">;
export type WebhookCreate = Schemas["WebhookCreate"];
export type WebhookCreated = Webhook & { secret: string };
export type WebhookEvent = Schemas["WebhookCreate"]["events"][number];
export type WebhookDelivery = Datified<Schemas["WebhookDelivery"], "created_at">;

export function useWebhookList() {
  return useQuery<Webhook[]>({
    queryKey: ["webhooks"],
    queryFn: async () => {
      const res = await apiGet<{ items: Webhook[] }>("/webhooks");
      return res.items;
    },
    refetchInterval: POLL_BASE_MS,
  });
}

export function useWebhookDeliveries(id: string | null) {
  return useQuery<WebhookDelivery[]>({
    queryKey: ["webhooks", "deliveries", id],
    queryFn: async () => {
      const res = await apiGet<{ items: WebhookDelivery[] }>(
        `/webhooks/${id}/deliveries`,
      );
      return res.items;
    },
    enabled: !!id,
    // A pending delivery is mid-retry, so its attempts and last error move on
    // their own; once nothing is pending the list still drops to the baseline
    // rather than stopping, because new deliveries arrive here too.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((d) => d.state === "pending")
        ? POLL_LIVE_MS
        : POLL_BASE_MS,
  });
}

export function useCreateWebhook() {
  const qc = useQueryClient();
  return useMutation<WebhookCreated, unknown, WebhookCreate>({
    meta: { suppressErrorToast: true },
    mutationFn: (input) => apiPost<WebhookCreated>("/webhooks", input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["webhooks"] }),
  });
}

export function useDeleteWebhook() {
  const qc = useQueryClient();
  return useMutation<void, unknown, { id: string }>({
    meta: { suppressErrorToast: true },
    mutationFn: ({ id }) => apiDelete<void>(`/webhooks/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["webhooks"] }),
  });
}
